import { reducedStream, reducer, streamReducer } from '@ardoq/rxbeach';
import {
  combineLatest,
  combineLatestWith,
  map,
  distinctUntilChanged,
  Observable,
} from 'rxjs';
import { activeView$ } from 'streams/views/mainContent/activeView$';
import { viewpointModeViews$ } from 'views/modeViews$';
import {
  setFolderId,
  setInitialMetaInfo,
  setTraversalDescription,
  setTraversalName,
  resetMetaInfoState,
} from './metaInfoActions';
import { viewpointMetaInfoOperations } from './viewpointMetaInfoOperations';
import { MetaInfoState } from './metaInfoTypes';
import { setStateProperty } from '@ardoq/common-helpers';
import { SelectedViewState } from '../selectedView/types';
import { ViewpointFormattingProps } from '../formattingTabs/viewpointFormattingTypes';
import { GroupingRulesState } from '../addGroupingRulesTab/groupingRulesTypes';
import { ViewIds } from '@ardoq/api-types';
import { isEqual } from 'lodash';

const dependencies$ = combineLatest({
  activeViews: activeView$,
  viewpointModeViews: viewpointModeViews$,
});

const getSelectedViewWarningDependency$ = (
  selectedView$: Observable<SelectedViewState>,
  viewpointFormatting$: Observable<ViewpointFormattingProps>,
  viewpointGroupingRules$: Observable<GroupingRulesState>
) =>
  combineLatest({
    selectedView: selectedView$,
    viewpointFormatting: viewpointFormatting$,
    viewpointGrouping: viewpointGroupingRules$,
  }).pipe(
    map(({ selectedView, viewpointFormatting, viewpointGrouping }) => ({
      selectedView: selectedView.selectedView?.value || ViewIds.NONE,
      labelFormatting: viewpointFormatting.multiLabelFormatting.labelFormatting,
      groupingRules: viewpointGrouping.groupingRules,
    })),
    distinctUntilChanged((a, b) => isEqual(a, b))
  );

export const createMetaInfo$ = (
  namespace: string,
  selectedView$: Observable<SelectedViewState>,
  viewpointFormatting$: Observable<ViewpointFormattingProps>,
  viewpointGroupingRules$: Observable<GroupingRulesState>
): Observable<MetaInfoState> =>
  reducedStream<MetaInfoState>(
    `viewpointBuilder2.0/metaInfo$:${namespace}`,
    viewpointMetaInfoOperations.getInitialMetaInfoState(),
    [
      streamReducer(
        dependencies$,
        viewpointMetaInfoOperations.processDependencies
      ),
      streamReducer(selectedView$, viewpointMetaInfoOperations.setSelectedView),
      streamReducer(
        getSelectedViewWarningDependency$(
          selectedView$,
          viewpointFormatting$,
          viewpointGroupingRules$
        ),
        viewpointMetaInfoOperations.setSelectedViewWarning
      ),
      reducer(setTraversalName, viewpointMetaInfoOperations.setTraversalName),
      reducer(setInitialMetaInfo, viewpointMetaInfoOperations.setInitialState),
      reducer(
        setTraversalDescription,
        viewpointMetaInfoOperations.setTraversalDescription
      ),
      reducer(setFolderId, setStateProperty('folderId')),
      reducer(
        // This reducer is mainly for the test
        resetMetaInfoState,
        viewpointMetaInfoOperations.resetMetaInfoState
      ),
    ],
    { namespace }
  ).pipe(
    combineLatestWith(viewpointModeViews$),
    map(([state, { byIds }]) =>
      viewpointMetaInfoOperations.stateWithSavableAttributes(state, byIds)
    )
  );
